/**
 * DATORACODE - Bundle-Auswahl auf der PDP
 *
 * Farbkombinationen, nachgeladene Produktzeilen, Preissumme, Deep-Link und Kaufweg.
 *
 * Die Zeilen kommen aus /products/<handle>?view=bundle-component[&variant=<id>] — nur die Seite
 * des Einzelprodukts weiss, welche Groessen es in einer Farbe gibt und welche lieferbar sind.
 *
 * Veraltete Antworten werden ueber einen Zaehler PRO ZEILE verworfen, nicht ueber einen globalen:
 * ein Groessenklick in Zeile B darf die laufende Antwort fuer Zeile A nicht kippen, sonst behielte
 * A einen veralteten Preis. Der UI-Zustand wird ausschliesslich in update() aus dem DOM abgeleitet,
 * damit keine verworfene Antwort den Kaufbutton gesperrt zuruecklaesst.
 *
 * Abhaengigkeiten (alle optional abgefragt): formatPrice/setPriceText/updateProductGridDiscounts
 * aus datora.js, publish/PUB_SUB_EVENTS aus pubsub.js/constants.js.
 */

if (!customElements.get('bundle-selector')) {
  customElements.define(
    'bundle-selector',
    class BundleSelector extends HTMLElement {
      constructor() {
        super();
        this.cache = window.bundleRowCache = window.bundleRowCache || new Map();
        this.submitting = false;
        this.loadErrorShown = false;
      }

      connectedCallback() {
        this.combos = Array.from(this.querySelectorAll('[data-bundle-combo]'));
        this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'));
        this.parentVariantId = this.dataset.parentVariantId;
        this.discountPercent = Number(this.dataset.discountPercent) || 0;

        this.addEventListener('click', this.onClick.bind(this));

        const submit = this.buyButton;
        if (submit) submit.addEventListener('click', this.onSubmit.bind(this));

        // Zeigte ?cs= auf eine Kombination, die es nicht mehr gibt oder die ausgeblendet ist, wird
        // die Adresse auf die tatsaechlich geoeffnete korrigiert — sonst bliebe ein Link stehen,
        // der etwas anderes verspricht als die Seite zeigt.
        const wanted = new URLSearchParams(window.location.search).get('cs');
        const initial = this.resolveInitialCombo();
        const staleDeepLink = !!wanted && !!initial && initial.dataset.cs !== wanted;
        this.selectCombo(initial, { updateUrl: staleDeepLink });
      }

      /* ============================
         Elemente ausserhalb des Blocks
         ============================ */

      get productInfo() {
        return this.closest('product-info') || document;
      }

      get priceEl() {
        return this.productInfo.querySelector('[data-bundle-price]');
      }

      get buyButton() {
        return this.productInfo.querySelector('[data-bundle-submit]');
      }

      get errorWrapper() {
        return this.productInfo.querySelector('[data-bundle-form] .product-form__error-message-wrapper');
      }

      get availabilityEl() {
        return this.productInfo.querySelector('[data-bundle-availability]');
      }

      /* ============================
         Kombinationen
         ============================ */

      // Deep-Link ?cs=<metaobject_id>. Unbekannt, geloescht oder ausgeblendet faellt auf die erste
      // sichtbare Kombination zurueck — ausgeblendete sind serverseitig gar nicht gerendert.
      resolveInitialCombo() {
        const wanted = new URLSearchParams(window.location.search).get('cs');
        if (wanted) {
          const match = this.combos.find((c) => c.dataset.cs === wanted);
          if (match) return match;
        }
        return this.combos[0] || null;
      }

      selectCombo(comboEl, { updateUrl = true } = {}) {
        this.currentCombo = comboEl;

        this.combos.forEach((c) => c.setAttribute('aria-pressed', String(c === comboEl)));

        // Farbnamen und Galerie wirken sofort, die Groessen folgen mit dem Abruf.
        const names = this.querySelector('[data-bundle-combo-names]');
        if (names && comboEl) names.textContent = comboEl.dataset.colorNames || '';

        if (comboEl) {
          this.swapGallery(comboEl);
          if (updateUrl) this.writeDeepLink(comboEl.dataset.cs);
        }

        this.loadRows(comboEl);
      }

      writeDeepLink(cs) {
        if (!cs) return;
        const url = new URL(window.location.href);
        url.searchParams.set('cs', cs);
        // replaceState, damit der Zurueck-Knopf die Seite verlaesst und nicht durch die
        // angesehenen Kombinationen wandert.
        window.history.replaceState({}, '', url);
      }

      // pdp-gallery.js steht im Markup weit hinter bundle-selector.js, beide mit defer — deferred
      // Skripte laufen in Dokumentreihenfolge. Beim ersten Aufruf aus connectedCallback ist das
      // Element also im DOM, aber noch nicht aufgewertet: handleVariantChange gibt es noch nicht.
      // Der Tausch brach still ab, und ein Deep-Link ?cs= zeigte die Bilder der ersten Kombination.
      // Auf die Definition warten statt auf die Einbindungsreihenfolge zu bauen — die haengt an der
      // Position im Markup und bricht beim naechsten Umbau wieder.
      swapGallery(comboEl) {
        const gallery = this.productInfo.querySelector('pdp-gallery');
        if (!gallery) return;

        if (typeof gallery.handleVariantChange !== 'function') {
          customElements.whenDefined('pdp-gallery').then(() => {
            // Ein zwischenzeitlicher Wechsel gewinnt: sonst setzte die spaeter aufgeloeste, aeltere
            // Zusage die Galerie der falschen Kombination.
            if (comboEl !== this.currentCombo) return;
            const upgraded = this.productInfo.querySelector('pdp-gallery');
            if (!upgraded || typeof upgraded.handleVariantChange !== 'function') return;
            this.applyGallery(upgraded, comboEl);
          });
          return;
        }

        this.applyGallery(gallery, comboEl);
      }

      applyGallery(gallery, comboEl) {
        const tpl = this.productInfo.querySelector(`template[data-bundle-gallery="${comboEl.dataset.cs}"]`);
        if (!tpl) return;

        const holder = document.createElement('div');
        holder.appendChild(tpl.content.cloneNode(true));
        // Ein Bundle hat keine variantenbezogene Medien-ID — zweites Argument bleibt leer.
        gallery.handleVariantChange(holder, null);
      }

      /* ============================
         Abrufe
         ============================ */

      // Muss zeichengleich zur Form im Inline-Vorabruf in bundle-selector.liquid bleiben.
      rowUrl(row, entryVariantId) {
        const vid = entryVariantId && entryVariantId !== '-' ? entryVariantId : '';
        return row.dataset.url + '?view=bundle-component' + (vid ? '&variant=' + vid : '');
      }

      fetchRow(url) {
        if (!this.cache.has(url)) {
          this.cache.set(
            url,
            fetch(url).then((r) => {
              if (!r.ok) throw new Error('bundle row ' + r.status);
              return r.text();
            })
          );
        }
        return this.cache.get(url);
      }

      nextToken(row) {
        const token = String((Number(row.dataset.rowGen) || 0) + 1);
        row.dataset.rowGen = token;
        return token;
      }

      // Kombinationswechsel: alle Zeilen laden. Die Zeilen gehen in den Ladezustand, damit der
      // Preis auf Platzhalter zurueckfaellt — Farben kosten unterschiedlich, die Zahl wuerde springen.
      loadRows(comboEl) {
        const entries = comboEl ? (comboEl.dataset.entryVariants || '').split(',') : [];
        const prefetched = this.takePrefetch(comboEl);
        this.rows.forEach((row) => row.classList.add('bundle-row--loading'));
        this.update();
        return Promise.all(this.rows.map((row, i) => this.loadRow(row, entries[i], prefetched && prefetched[i])));
      }

      // Der Inline-Vorabruf legt seine Promises nach Slot-Index ab. Einmalig verbrauchen und nur,
      // wenn die geoeffnete Kombination die vorgeladene ist.
      takePrefetch(comboEl) {
        const store = window.bundleRowPrefetch;
        if (!store) return null;
        delete window.bundleRowPrefetch;
        if (!comboEl || store.cs !== comboEl.dataset.cs) return null;
        return store.rows;
      }

      // Groessenklick: nur diese Zeile, ohne Ladezustand. Preise unterscheiden sich nach Farbe,
      // nicht nach Groesse — die angezeigte Zahl bleibt also stehen statt zu blinken.
      async loadRow(row, variantId, prefetched) {
        const token = this.nextToken(row);
        const url = this.rowUrl(row, variantId);
        // Merker, dass ein Abruf laeuft — OHNE sichtbaren Ladezustand. Der Groessenklick laedt
        // bewusst still nach, damit der Preis nicht blinkt; bis die Antwort da ist, traegt die Zeile
        // aber noch die alte Varianten-ID. Ohne diesen Merker bliebe der Kaufbutton aktiv und ein
        // schneller Klick legte die vorherige Groesse in den Warenkorb.
        row.dataset.pending = token;
        // Sofort neu bewerten: update() lief bisher erst NACH dem await, der Button blieb also
        // waehrend des ganzen Abrufs aktiv.
        this.update();
        // Vorgeladene Antwort auch in den Zwischenspeicher legen, damit der Rueckweg auf diese
        // Kombination spaeter gratis bleibt.
        if (prefetched && !this.cache.has(url)) this.cache.set(url, prefetched);
        try {
          const html = await (prefetched || this.fetchRow(url));
          if (token !== row.dataset.rowGen) return; // veraltete Antwort verwerfen
          // Ueber einen Wurf in denselben Fehlerpfad: eine unbrauchbare 200er-Antwort ist fuer die
          // Zeile genauso ein Fehlschlag wie ein 404, und beide brauchen dieselbe Behandlung.
          if (!this.renderRow(row, html)) throw new Error('bundle row payload missing');
        } catch (e) {
          // Fehlgeschlagenen Abruf nicht behalten, sonst bleibt die Zeile dauerhaft leer.
          this.cache.delete(url);
          // Ladezustand beenden und kennzeichnen: sonst lief der Schimmer endlos und der Kaufbutton
          // blieb ohne erkennbaren Grund gesperrt. Die Zeile traegt weiterhin kein data-slot-price,
          // ready bleibt also false und der Button korrekt gesperrt — nur eben sichtbar begruendet.
          row.classList.remove('bundle-row--loading');
          row.classList.add('bundle-row--failed');
          this.showLoadError();
          console.error('[bundle-selector]', e);
        } finally {
          // Nur den eigenen Merker zuruecknehmen. Ein neuerer Abruf hat ihn bereits mit seinem Token
          // ueberschrieben — dann darf dieser hier ihn nicht wegraeumen und den Button freigeben,
          // waehrend die neuere Antwort noch unterwegs ist.
          if (row.dataset.pending === token) delete row.dataset.pending;
        }
        this.update();
      }

      // Gibt zurueck, ob die Zeile ersetzt werden konnte. fetchRow wirft nur bei !r.ok — eine Antwort
      // mit Status 200 aber unbrauchbarem Koerper (Cloudflare-Zwischenseite, Proxy-Seite, umbenanntes
      // Template) kaeme hier still an und liesse die Zeile sonst dauerhaft im Ladezustand stehen.
      renderRow(row, html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('.bundle-row__payload');
        const current = row.querySelector('.bundle-row__payload');
        if (!next || !current) return false;
        current.replaceWith(next);
        row.classList.remove('bundle-row--loading');
        row.classList.remove('bundle-row--failed');
        this.clearLoadError();
        return true;
      }

      /* ============================
         Zustand — einzige Quelle der Wahrheit
         ============================ */

      get selection() {
        return this.rows.map((row) => {
          const payload = row.querySelector('[data-slot-price]');
          const loading = row.classList.contains('bundle-row--loading');
          // pending deckt den stillen Nachladevorgang beim Groessenklick ab: optisch aendert sich
          // nichts, die angezeigten Daten sind aber veraltet, bis die Antwort da ist.
          const pending = !!row.dataset.pending;
          // Gescheiterter Abruf. Beim Groessenwechsel behaelt die Zeile ihr altes Payload — die
          // Markierung zeigt dann schon die neue Groesse, die Daten gehoeren aber noch zur alten.
          // Ohne diese Bedingung waere der Kaufbutton wieder frei und der Kunde kaufte die falsche.
          const failed = row.classList.contains('bundle-row--failed');
          return {
            row,
            variantId: payload ? payload.dataset.slotVariantId : '',
            price: payload ? Number(payload.dataset.slotPrice) || 0 : 0,
            // Eigenes Flag statt price != 0: 0 ist ein gueltiger Preis (kostenlose Komponente) und
            // darf nicht als "unbekannt" durchgehen.
            priced: payload ? payload.dataset.slotPriced === 'true' : false,
            available: payload ? payload.dataset.slotAvailable === 'true' : false,
            hasPayload: !!payload,
            loading,
            pending,
            failed,
            ready: !!payload && !loading && !pending && !failed,
          };
        });
      }

      // Preis und Kaufbarkeit werden getrennt entschieden. Der Preis haengt an der Farbe, nicht an
      // der Groesse: ein stiller Nachladevorgang macht die angezeigte Summe nicht ungueltig, wohl
      // aber die Varianten-ID — deshalb sperrt er den Kaufbutton, ohne den Preis anzutasten.
      update() {
        const selection = this.selection;
        const has = selection.length > 0;
        const anyLoading = selection.some((s) => s.loading);

        // Bezifferbar: jede Zeile hat ein Payload mit explizit bekanntem Preis (data-slot-priced).
        // Ein Preis von 0 ist gueltig (kostenlose Komponente) und zaehlt als bekannt — deshalb wird
        // hier das Flag geprueft, nicht die Zahl selbst. Bleiben bei einer Farbe ausschliesslich
        // ausgeblendete Groessen uebrig, bleibt priced false — das ist dann ein Datenproblem, keine
        // Anzeigefrage, und die Summe faellt wie bisher auf den Ladezustand zurueck.
        const priced = has && selection.every((s) => s.hasPayload && !s.loading && s.priced);

        const price = this.priceEl;
        if (price) {
          if (priced) {
            this.updatePrice(selection);
          } else {
            price.classList.add('price--bundle-loading');
            // Schimmer nur, solange wirklich geladen wird. Eine ausverkaufte Kombination ist fertig,
            // nur eben nicht bezifferbar — dort liefe die Animation sonst endlos.
            price.classList.toggle('price--bundle-settled', !anyLoading);
          }
        }

        // Sind Kombinationen konfiguriert, aber alle ausgeblendet, gibt es keinen Streifen und das
        // Bundle ist nicht kaufbar. Ganz ohne Kombination bleibt es kaufbar (CFSN-642 laesst das zu).
        const configured = Number(this.dataset.colorSetCount) || 0;
        const noVisibleCombo = configured > 0 && this.combos.length === 0;
        const comboAvailable =
          !noVisibleCombo && (!this.currentCombo || this.currentCombo.dataset.available === 'true');

        // Verkaufbar nach Datenlage, unabhaengig davon ob gerade abgerufen wird. failed schliesst den
        // Fall aus, dass eine Zeile nach einem gescheiterten Groessenwechsel noch die alten Daten
        // traegt, die Markierung aber schon die neue Groesse zeigt.
        const sellable =
          has &&
          comboAvailable &&
          selection.every((s) => s.hasPayload && !s.failed && s.available && s.variantId);
        // Beschaeftigt: waehrend eines Abrufs traegt die Zeile noch die alte Varianten-ID, ein Kauf
        // in diesem Fenster legte die vorherige Groesse in den Warenkorb.
        const busy = has && selection.some((s) => s.loading || s.pending);

        this.updateAvailability(sellable);
        this.updateBuyButton(sellable, busy);
      }

      updatePrice(selection) {
        const price = this.priceEl;
        if (!price) return;

        const total = selection.reduce((sum, s) => sum + s.price, 0);
        const discounted = Math.round((total * (100 - this.discountPercent)) / 100);

        // -regular sitzt in price__regular, dem Block fuer "nicht im Sale". Dort steht der Betrag, den
        // der Kunde zahlt, also der rabattierte — nicht die Komponentensumme. Sichtbar wird der
        // Unterschied bei Standard-Darstellung mit hide_compare_prices, wo die Sale-Klasse fehlt.
        setBundlePriceText(price.querySelector('[data-bundle-price-regular]'), bundleMoney(discounted));
        setBundlePriceText(price.querySelector('[data-bundle-price-final]'), bundleMoney(discounted));
        // -compare ist der Streichpreis und traegt als einziger die unrabattierte Summe.
        setBundlePriceText(price.querySelector('[data-bundle-price-compare]'), bundleMoney(total));

        // Erst jetzt wird das Element fuer Datora sichtbar: data-datora-product-id ist dessen
        // Discovery-Selektor und fehlt in Liquid bewusst, damit vorher nichts gegen einen leeren
        // Preis gerechnet wird. Basis ist der Bundle-Endpreis — ein Function-Rabatt kommt laut
        // Ticket "zusaetzlich" dazu.
        price.dataset.datoraPrice = String(discounted);
        price.dataset.datoraCompare = String(total);
        if (!price.dataset.datoraProductId && price.dataset.bundleProductId) {
          price.dataset.datoraProductId = price.dataset.bundleProductId;
        }
        price.classList.remove('price--bundle-loading', 'price--bundle-settled');

        if (typeof co2 !== 'undefined' && typeof updateProductGridDiscounts === 'function') {
          // Die Funktion ist async und awaitet co2.getDiscountCode() ausserhalb ihres try/catch —
          // ohne catch hier waere ein Fehlschlag eine unbehandelte Rejection.
          Promise.resolve(updateProductGridDiscounts(this.productInfo)).catch((e) =>
            console.error('[bundle-selector] datora', e)
          );
        }
      }

      // Lieferzeit: der Text ist shopweit konstant und steht im DOM — nur die Sichtbarkeit wechselt.
      updateAvailability(available) {
        const el = this.availabilityEl;
        if (el) el.classList.toggle('hidden', !available);
      }

      updateBuyButton(sellable, busy) {
        const button = this.buyButton;
        if (!button || this.submitting) return;
        button.disabled = !sellable || busy;
        const label = button.querySelector('span');
        if (!label) return;
        // Waehrend eines Abrufs bleibt die Beschriftung stehen: die Kombination ist nicht
        // ausverkauft, sie ist nur noch nicht bestaetigt. Sonst spraenge der Text bei jedem
        // Groessenklick kurz auf "Ausverkauft".
        if (busy) return;
        const next = sellable ? button.dataset.labelAddToCart : button.dataset.labelSoldOut;
        if (next) label.textContent = next;
      }

      /* ============================
         Interaktion
         ============================ */

      onClick(event) {
        const combo = event.target.closest('[data-bundle-combo]');
        if (combo && this.combos.includes(combo)) {
          if (combo !== this.currentCombo) this.selectCombo(combo);
          return;
        }

        const toggle = event.target.closest('[data-bundle-details-toggle]');
        if (toggle) {
          this.toggleDetails(toggle);
          return;
        }

        const size = event.target.closest('.bundle-size');
        if (size && !size.disabled) this.onSizeClick(size);
      }

      // Aufklapper ohne <details>: dort bestimmte der Inhalt die Breite seines Elternelements mit und
      // verschob beim Aufklappen die Groessentabelle daneben. Jetzt Button plus getrennter Container,
      // verbunden ueber aria-controls — der Zustand steht in aria-expanded und im hidden-Attribut.
      toggleDetails(button) {
        const body = document.getElementById(button.getAttribute('aria-controls'));
        if (!body) return;
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        body.toggleAttribute('hidden', isOpen);
      }

      onSizeClick(button) {
        const row = button.closest('[data-bundle-row]');
        if (!row) return;

        const group = button.closest('.bundle-row__sizes');
        if (group) {
          group.querySelectorAll('.bundle-size').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        }

        this.loadRow(row, button.dataset.variantId);
      }

      /* ============================
         In den Warenkorb
         ============================ */

      showError(message) {
        const wrapper = this.errorWrapper;
        if (!wrapper) return;
        const target = wrapper.querySelector('.product-form__error-message');
        if (target) target.textContent = message || '';
        wrapper.toggleAttribute('hidden', !message);
      }

      showLoadError() {
        this.loadErrorShown = true;
        this.showError(this.dataset.loadError || '');
      }

      // Nur die eigene Lademeldung zuruecknehmen. Eine Warenkorb-Meldung aus onSubmit muss stehen
      // bleiben: dort laufen direkt danach die Zeilen neu, und ein erfolgreicher Abruf wuerde den
      // Grund des Fehlschlags sonst wegwischen, bevor der Kunde ihn lesen kann.
      clearLoadError() {
        if (!this.loadErrorShown) return;
        if (this.querySelector('.bundle-row--failed')) return;
        this.loadErrorShown = false;
        this.showError('');
      }

      setSubmitting(isSubmitting) {
        this.submitting = isSubmitting;
        const button = this.buyButton;
        if (!button) return;
        button.classList.toggle('loading', isSubmitting);
        if (isSubmitting) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isSubmitting);
      }

      async onSubmit(event) {
        event.preventDefault();
        const button = this.buyButton;
        if (!button || button.disabled || this.submitting) return;

        const selection = this.selection;
        if (!selection.every((s) => s.ready && s.variantId)) return;

        this.showError('');
        this.setSubmitting(true);

        // Ein Zeitstempel fuer das ganze Bundle. _bundleIndex haelt dasselbe Produkt in
        // verschiedenen Groessen getrennt, _bundleTS trennt zwei Exemplare desselben Bundles —
        // ohne ihn legt Shopify gleiche Varianten zusammen.
        const ts = String(Date.now());
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        // _bundleImage nur am ersten Platz und nur, wenn Liquid die Bildreferenz aufgeloest hat.
        // Die Cart Transform setzt damit das Bild der zusammengefassten Zeile. Da die Eigenschaft
        // zum Farbset gehoert, kommt sie aus der gerade aktiven Kombination und wechselt dadurch
        // beim Farbwechsel mit. Das Unterstrich-Praefix haelt sie in Warenkorb und Checkout unsichtbar.
        // Shopify schreibt absolute CDN-URLs im ausgelieferten HTML auf den shop-relativen Pfad um
        // (/cdn/shop/files/...). Die Cart Transform braucht eine aufloesbare absolute URL, deshalb
        // hier normalisieren — auf das JavaScript wirkt die Umschreibung nicht. Basis ist shopUrl
        // aus dem Layout, damit nicht die Dev-Server-Herkunft (127.0.0.1) hineingerechnet wird.
        const rawBundleImage = (this.currentCombo && this.currentCombo.dataset.bundleImage) || '';
        let bundleImage = '';
        if (rawBundleImage) {
          try {
            bundleImage = new URL(rawBundleImage, window.shopUrl || window.location.origin).href;
          } catch (urlError) {
            bundleImage = '';
          }
        }

        const items = selection.map((s, index) => {
          const properties = {
            _bundleId: this.parentVariantId,
            _bundleIndex: String(index),
            _bundleTS: ts,
          };
          if (index === 0 && bundleImage) properties._bundleImage = bundleImage;
          return { id: s.variantId, quantity: 1, properties };
        });

        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        try {
          // Der .js-Endpunkt, nicht routes.cart_add_url: nur er liefert bei einem Fehler
          // status/message/description im Koerper. /cart/add antwortet auf 422 ohne diese Felder,
          // der Kunde sah dann nur den generischen Rueckfalltext statt "X ist bereits ausverkauft".
          // Basis bleibt trotzdem routes, damit ein Sprach- oder Market-Praefix erhalten bleibt.
          const addBase = (window.routes && window.routes.cart_add_url) || '/cart/add';
          const addUrl = addBase.endsWith('.js') ? addBase : `${addBase}.js`;

          // Eigener Fangarm nur um die Anfrage: schlaegt sie fehl (offline, abgebrochen, DNS), ist
          // der Warenkorb unveraendert und die Meldung trifft zu. Ohne diese Trennung landete der
          // Fall im aeusseren catch, das nur loggt — der Kunde sah den Spinner stoppen und sonst nichts.
          let res = null;
          try {
            res = await fetch(addUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            });
          } catch (networkError) {
            console.error('[bundle-selector]', networkError);
            this.showError((window.cartStrings && window.cartStrings.error) || '');
            return; // finally setzt setSubmitting zurueck
          }
          // Nicht jede Antwort ist JSON: eine Cloudflare-Pruefseite oder eine Proxy-Fehlerseite
          // kommt als HTML, und res.json() wuerfe dann in den catch — ohne Meldung an den Kunden.
          let response = null;
          try {
            response = await res.json();
          } catch (parseError) {
            response = null;
          }

          // res.ok gehoert dazu, nicht nur response.status: /cart/add liefert bei 422 einen Koerper
          // OHNE status-Feld (der .js-Endpunkt setzt es, dieser nicht). Ohne die Pruefung liefe die
          // Erfolgsspur weiter, cart.renderContents bekaeme die Fehlerantwort ohne sections und
          // warf einen TypeError — der Kunde sah keine Meldung und der Drawer blieb kaputt.
          if (!res.ok || !response || response.status) {
            const errorMessage =
              (response && (response.description || response.message)) ||
              (window.cartStrings && window.cartStrings.error);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bundle-selector',
                productVariantId: this.parentVariantId,
                errors: (response && (response.errors || response.description)) || errorMessage,
                message: (response && response.message) || errorMessage,
              });
            }
            this.showError(errorMessage);
            // Der Warenkorb bleibt unveraendert; die Zeilen holen den aktuellen Bestand. Der
            // Zwischenspeicher muss dafuer weg — sonst kaeme dieselbe Antwort zurueck, die die
            // Groesse noch als lieferbar ausweist, und die betroffene Zeile aenderte sich nie.
            this.cache.clear();
            this.setSubmitting(false);
            await this.loadRows(this.currentCombo);
            return;
          }

          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);

          // An cartUpdate haengt die Datora Storefront Library: datora.js uebersetzt es in
          // cart.update, co2 rechnet Rabatte, Streichpreise und Gratisartikel-Schwellen neu.
          // Als productVariantId erwarten die Abonnenten eine einzelne ID.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-selector',
              productVariantId: this.parentVariantId,
              cartData: response,
            });
          }
        } catch (e) {
          // Hier ist die Anfrage bereits durch: die Artikel LIEGEN im Warenkorb, geworfen hat
          // renderContents oder ein Abonnent. Eine Fehlermeldung waere an dieser Stelle unwahr und
          // wuerde zum zweiten Kaufversuch verleiten, deshalb bewusst nur geloggt — dasselbe
          // Verhalten wie in product-form.js.
          console.error('[bundle-selector]', e);
        } finally {
          this.setSubmitting(false);
        }
      }
    }
  );
}

// Geld ueber die Theme-Helfer aus datora.js — moneyFormat kann HTML enthalten (transcy-money).
function bundleMoney(cents) {
  if (typeof formatPrice === 'function') return formatPrice(cents);
  return String(cents / 100);
}

function setBundlePriceText(el, html) {
  if (!el) return;
  if (typeof setPriceText === 'function') setPriceText(el, html);
  else el.innerHTML = html;
}
